import { A_USDT_ADDRESS, USDT_ADDRESS } from '@/config/card'
import { hyperEVM } from '@/config/chain/constants'
import { type VaultMarket, getVaultMarket } from '@/features/evault-details/data/products'
import type { Address } from 'viem'

export type CardMode = 'borrow' | 'spend'
export type ProtocolId = 'hypurrFi' | 'euler'

export type SolverType = 'hypurrFi' | 'euler' | 'hypurrFi-withdraw' | 'euler-withdraw'

export const CREDIT_TYPES = {
  HYPURRFI_BORROW: 'hypurrFi',
  HYPURRFI_SPEND: 'hypurrFi-withdraw',
  EULER_BORROW: 'euler',
  EULER_SPEND: 'euler-withdraw',
} as const satisfies Record<string, SolverType>

export const DEFAULT_MAX_CARDS_FALLBACK = 3
export const KYC_AUTO_POLL_INTERVAL_MS = 30_000
export const MAX_CARD_LIMIT = 100_000 // must match backend maxCardLimit (10_000_000 cents)

// --- Per-card protocol config resolver ---
// Each card has its own solver type, and the UI behavior differs per solver.

export type DelegationType = 'pooled' | 'euler' | 'euler-withdraw' | 'spend-allowance' | 'none'
export type MarketDataSource = 'hypurrFi' | 'euler' | 'none'

export interface ActiveCardProtocolConfig {
  solverType: string
  protocol: string
  displayName: string
  mode: CardMode
  chainId: number
  /** Type of on-chain delegation/authorization needed */
  delegationType: DelegationType
  /** Whether we have on-chain market data (spending power, health factor) */
  hasMarketData: boolean
  /** Which market data hook to use */
  marketDataSource: MarketDataSource
  /** Borrow token address for market data hooks */
  borrowTokenAddress?: string
  /** Borrow vault address for Euler */
  borrowVaultAddress?: string
  /** Supply vault address for Euler withdraw/spend */
  supplyVaultAddress?: string
  /** Sub-account ID for Euler (from card's solverConfig) */
  eulerSubAccountId?: number
  configError?: string
}

export type MarketResolver = (vaultAddress: Address) => VaultMarket | undefined

/**
 * Derive the full protocol config for a specific card.
 * Accepts an optional `resolveMarket` function (from `useGetVaultMarket`)
 * to classify vaults using indexer data. Falls back to the hardcoded map.
 */
export function getActiveCardConfig(
  card: {
    solverType: string
    solverConfig?: Record<string, any>
    solverCreditProviderAddress?: string
  },
  resolveMarket?: MarketResolver,
): ActiveCardProtocolConfig {
  const info = SOLVER_DISPLAY_MAP[card.solverType]
  const eulerSubAccountId = card.solverConfig?.subAccountId as number | undefined

  switch (card.solverType) {
    case 'hypurrFi': {
      const borrowTokenAddress = card.solverConfig?.borrowTokenAddress as string | undefined

      return {
        solverType: card.solverType,
        protocol: 'Pooled',
        displayName: 'Pooled',
        mode: 'borrow',
        chainId: hyperEVM.id,
        delegationType: 'pooled',
        hasMarketData: true,
        marketDataSource: 'hypurrFi',
        borrowTokenAddress: borrowTokenAddress || HYPURRFI_TOKENS.USDT,
      }
    }
    case 'euler': {
      const vaultAddress = (card.solverConfig?.borrowVaultAddress as string) || card.solverCreditProviderAddress || ''
      const isPrime = vaultAddress ? isPrimeVault(vaultAddress, resolveMarket) : false
      const borrowTokenAddress =
        (card.solverConfig?.borrowTokenAddress as string | undefined) ||
        (card.solverConfig?.baseTokenAddress as string | undefined) ||
        EULER_TOKENS.USDC
      return {
        solverType: card.solverType,
        protocol: isPrime ? 'Prime' : 'Yield',
        displayName: isPrime ? 'Prime' : 'Yield',
        mode: 'borrow',
        chainId: hyperEVM.id,
        delegationType: 'euler',
        hasMarketData: true,
        marketDataSource: 'euler',
        borrowTokenAddress,
        borrowVaultAddress: vaultAddress,
        eulerSubAccountId: eulerSubAccountId ?? EULER_CARD_SUB_ACCOUNT_ID,
      }
    }
    case 'euler-withdraw': {
      const vaultAddress = (card.solverConfig?.borrowVaultAddress as string) || card.solverCreditProviderAddress || ''
      const isPrime = vaultAddress ? isPrimeVault(vaultAddress, resolveMarket) : false
      const borrowTokenAddress =
        (card.solverConfig?.borrowTokenAddress as string | undefined) ||
        (card.solverConfig?.baseTokenAddress as string | undefined) ||
        EULER_TOKENS.USDC
      const supplyVaultAddress = card.solverConfig?.supplyVaultAddress as string | undefined
      return {
        solverType: card.solverType,
        protocol: isPrime ? 'Prime' : 'Yield',
        displayName: isPrime ? 'Prime' : 'Yield',
        mode: 'spend',
        chainId: hyperEVM.id,
        delegationType: 'euler-withdraw',
        hasMarketData: true,
        marketDataSource: 'euler',
        borrowTokenAddress,
        borrowVaultAddress: vaultAddress,
        supplyVaultAddress,
        eulerSubAccountId: eulerSubAccountId ?? EULER_CARD_SUB_ACCOUNT_ID,
      }
    }
    case 'hypurrFi-withdraw': {
      const borrowTokenAddress =
        (card.solverConfig?.baseTokenAddress as string | undefined) ||
        (card.solverConfig?.borrowTokenAddress as string | undefined)

      return {
        solverType: card.solverType,
        protocol: 'Pooled',
        displayName: 'Pooled',
        mode: 'spend',
        chainId: hyperEVM.id,
        delegationType: 'spend-allowance',
        hasMarketData: true,
        marketDataSource: 'hypurrFi',
        borrowTokenAddress: borrowTokenAddress || HYPURRFI_TOKENS.USDT,
      }
    }
    default: {
      return {
        solverType: card.solverType,
        protocol: info?.protocol ?? card.solverType,
        displayName: info?.displayName ?? card.solverType,
        mode: info?.mode ?? 'borrow',
        chainId: info?.chainId ?? hyperEVM.id,
        delegationType: 'none',
        hasMarketData: false,
        marketDataSource: 'none',
      }
    }
  }
}

export interface ProtocolConfig {
  id: ProtocolId
  displayName: string
  icon: string
  enabled: boolean
  borrowCreditType: string
  spendCreditType?: string
  chainId: number
  borrowTokenAddress: string
  borrowVaultAddress?: string
}

export const HYPURRFI_TOKENS = {
  USDT: USDT_ADDRESS,
  A_USDT: A_USDT_ADDRESS,
} as const

export const STABLECOIN_TOKENS = {
  USDT0: '0xB8CE59FC3717ada4C02eaDF9682A9e934F625ebb' as const,
  USDC: '0xb88339CB7199b77E23DB6E890353e22632Ba630f' as const,
  USDe: '0x5d3a1Ff2b6BAb83b63cd9AD0787074081a52ef34' as const,
  USDH: '0x111111a1a0667d36bD57c0A9f569b98057111111' as const,
  USDXL: '0xca79db4B49f608eF54a5CB813FbEd3a6387bC645' as const,
} as const

export const STABLECOIN_DECIMALS: Record<string, number> = {
  [STABLECOIN_TOKENS.USDT0]: 6,
  [STABLECOIN_TOKENS.USDC]: 6,
  [STABLECOIN_TOKENS.USDe]: 18,
  [STABLECOIN_TOKENS.USDH]: 6,
  [STABLECOIN_TOKENS.USDXL]: 18,
}

export const STABLECOIN_LABELS: Record<string, string> = {
  [STABLECOIN_TOKENS.USDT0.toLowerCase()]: 'USDT0',
  [STABLECOIN_TOKENS.USDC.toLowerCase()]: 'USDC',
  [STABLECOIN_TOKENS.USDe.toLowerCase()]: 'USDe',
  [STABLECOIN_TOKENS.USDH.toLowerCase()]: 'USDH',
  [STABLECOIN_TOKENS.USDXL.toLowerCase()]: 'USDXL',
}

export const EULER_TOKENS = {
  USDC: '0xb88339cb7199b77e23db6e890353e22632ba630f' as const,
} as const

export interface EulerVaultInfo {
  address: string
  borrowToken: string
  borrowTokenSymbol: string
  label: string
}

export function isYieldVault(address: string, resolveMarket?: MarketResolver): boolean {
  const market = resolveMarket ? resolveMarket(address as Address) : getVaultMarket(address as Address)
  return market?.key === 'hypurrfi-yield'
}

export function isPrimeVault(address: string, resolveMarket?: MarketResolver): boolean {
  const market = resolveMarket ? resolveMarket(address as Address) : getVaultMarket(address as Address)
  return market?.key === 'hypurrfi-prime'
}

// Default sub-account for legacy single-card Euler flow.
// Multi-card: each card stores its own subAccountId in solverConfig.
export const EULER_CARD_SUB_ACCOUNT_ID = 169

export interface BorrowTokenOption {
  address: string
  symbol: string
  decimals: number
}

export interface SolverDisplayInfo {
  solverType: SolverType
  protocol: string
  displayName: string
  mode: CardMode
  chainId: number
}

export const SOLVER_DISPLAY_MAP: Record<string, SolverDisplayInfo> = {
  hypurrFi: {
    solverType: 'hypurrFi',
    protocol: 'Pooled',
    displayName: 'Pooled',
    mode: 'borrow',
    chainId: hyperEVM.id,
  },
  euler: {
    solverType: 'euler',
    protocol: 'Yield',
    displayName: 'Yield',
    mode: 'borrow',
    chainId: hyperEVM.id,
  },
  'hypurrFi-withdraw': {
    solverType: 'hypurrFi-withdraw',
    protocol: 'Pooled',
    displayName: 'Pooled',
    mode: 'spend',
    chainId: hyperEVM.id,
  },
  'euler-withdraw': {
    solverType: 'euler-withdraw',
    protocol: 'Yield',
    displayName: 'Yield',
    mode: 'spend',
    chainId: hyperEVM.id,
  },
}

export function getSolverDisplayName(solverType: string): string {
  return SOLVER_DISPLAY_MAP[solverType]?.displayName ?? solverType
}

export function getBorrowTokenLabel(address: string): string {
  return STABLECOIN_LABELS[address.toLowerCase()] ?? address.slice(0, 10)
}

const HOOK_TYPE_LABELS: Record<string, string> = {
  swap: 'Swap Hook',
  sponsored_spend: 'Sponsored Spend',
}

export function getHookTypeLabel(hookType: string): string {
  return HOOK_TYPE_LABELS[hookType] ?? hookType
}

export const PROTOCOL_CHAIN_IDS = {
  HYPURRFI: hyperEVM.id,
  EULER: hyperEVM.id,
} as const

export const DEFAULT_CARD_CONFIG = {
  chainId: PROTOCOL_CHAIN_IDS.HYPURRFI,
  solverType: CREDIT_TYPES.HYPURRFI_BORROW,
  borrowTokenAddress: HYPURRFI_TOKENS.USDT,
  hookConfig: {},
  hookType: '',
} as const

/**
 * Support email configuration
 */
export const SUPPORT_EMAIL = {
  ADDRESS: 'support@hypurrfi.com',
  SUBJECTS: {
    KYC_APPLICATION_SUPPORT: 'KYC Application Support',
    KYC_APPLICATION_ISSUE: 'KYC Application Issue',
  },
} as const

/**
 * Support URLs
 */
export const SUPPORT_URLS = {
  KYC_SUPPORT: `mailto:${SUPPORT_EMAIL.ADDRESS}?subject=${SUPPORT_EMAIL.SUBJECTS.KYC_APPLICATION_SUPPORT}`,
  KYC_ISSUE: `mailto:${SUPPORT_EMAIL.ADDRESS}?subject=${SUPPORT_EMAIL.SUBJECTS.KYC_APPLICATION_ISSUE}`,
} as const

/**
 * Default chain ID for token display (HypurrFi chain)
 */
export const DEFAULT_TOKEN_DISPLAY_CHAIN_ID = PROTOCOL_CHAIN_IDS.HYPURRFI

/**
 * RAIN application statuses
 */
export const RAIN_REJECTED_STATUSES = ['rejected', 'denied', 'locked', 'canceled']
export const RAIN_NEEDS_ACTION_STATUSES = ['needsInformation', 'needsVerification']
